import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { MapPin, Phone, Mail, Clock, ArrowRight, Send, CheckCircle2 } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { useSiteContent } from "@/hooks/useSiteContent";
import { Heading } from "@/data/siteContent";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, delay, ease: "easeOut" },
});

const Hero = ({ s }) => (
  <section data-testid="contact-hero" className="relative -mt-[80px] flex min-h-[42vh] items-center justify-center overflow-hidden lg:-mt-[78px]">
    <img
      src={s.contact_hero_image}
      alt="Wonder Events event"
      className="absolute inset-0 h-full w-full object-cover"
      loading="eager"
    />
    <div className="absolute inset-0 bg-[#1F1F1F]/70" />
    <div className="relative z-10 mx-auto max-w-4xl px-5 py-20 text-center">
      <motion.span
        {...fadeUp()}
        data-testid="contact-hero-eyebrow"
        className="inline-block rounded-full border border-white/30 bg-white/10 px-5 py-2 text-xs font-bold uppercase tracking-[0.28em] text-white backdrop-blur"
      >
        {s.contact_hero_eyebrow}
      </motion.span>
      <motion.h1
        {...fadeUp(0.12)}
        className="mt-5 text-4xl font-extrabold leading-[1.12] tracking-tight text-white sm:text-5xl lg:text-6xl"
      >
        <Heading value={s.contact_hero_heading} />
      </motion.h1>
      <motion.p {...fadeUp(0.22)} className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-[#E5E5E5] sm:text-lg">
        {s.contact_hero_text}
      </motion.p>
    </div>
  </section>
);

const InfoCard = ({ icon: Icon, title, children, testid }) => (
  <motion.div
    {...fadeUp(0.08)}
    data-testid={testid}
    className="group rounded-2xl border border-[#F0F0F0] bg-white p-6 text-center shadow-[0_6px_24px_rgba(0,0,0,0.05)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(198,40,40,0.1)] sm:text-left"
  >
    <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#C62828] text-white shadow-[0_8px_20px_rgba(198,40,40,0.3)] transition-transform duration-300 group-hover:scale-110 sm:mx-0">
      <Icon size={20} strokeWidth={2} />
    </span>
    <h3 className="mt-4 text-sm font-extrabold uppercase tracking-wider text-[#1F1F1F]">{title}</h3>
    <div className="mt-2 text-sm leading-relaxed text-[#666666]">{children}</div>
  </motion.div>
);

const inputCls =
  "w-full rounded-xl border border-[#E5E5E5] bg-white px-4 py-3 text-sm text-[#1F1F1F] outline-none transition-colors duration-200 placeholder:text-[#AAAAAA] focus:border-[#C62828] focus:ring-2 focus:ring-[#C62828]/15";

const Field = ({ label, required, children, testid }) => (
  <div data-testid={testid}>
    <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#666666]">
      {label} {required && <span className="text-[#C62828]">*</span>}
    </label>
    {children}
  </div>
);

const EnquiryForm = ({ s }) => {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", event_type: "", event_date: "", location: "", message: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const eventTypes = s.form_event_types.split(",").map((v) => v.trim()).filter(Boolean);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      if (s.web3forms_access_key) {
        const res = await fetch("https://api.web3forms.com/submit", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            access_key: s.web3forms_access_key,
            subject: `New Wonder Events Enquiry: ${form.event_type || "Event"}`,
            from_name: "Wonder Events Website",
            ...form,
            botcheck: false,
          }),
        });
        const result = await res.json().catch(() => ({}));
        if (!res.ok || result.success !== true) {
          throw new Error(result.message || "send failed");
        }
      } else if (s.form_endpoint.includes("contact-form-7")) {
        const data = new FormData();
        Object.entries(form).forEach(([key, value]) => data.append(key, value));
        const idMatch = s.form_endpoint.match(/contact-forms\/(\d+)\//);
        if (idMatch) data.append("_wpcf7_unit_tag", `wpcf7-f${idMatch[1]}-p0-o1`);
        const res = await fetch(s.form_endpoint, { method: "POST", body: data });
        const result = await res.json().catch(() => ({}));
        if (!res.ok || result.status !== "mail_sent") {
          throw new Error(result.message || "send failed");
        }
      } else {
        await axios.post(s.form_endpoint, { ...form, name: form.full_name });
      }
      setStatus("sent");
      setForm({ full_name: "", email: "", phone: "", event_type: "", event_date: "", location: "", message: "" });
    } catch (err) {
      setStatus("idle");
      setError("err");
    }
  };

  return (
    <motion.div
      {...fadeUp(0.1)}
      className="rounded-2xl bg-white p-7 shadow-[0_16px_45px_rgba(0,0,0,0.08)] ring-1 ring-[#F0F0F0] sm:p-9"
    >
      <h3 className="text-xl font-extrabold tracking-tight text-[#1F1F1F] sm:text-2xl">
        {s.contact_form_title}
      </h3>
      <span className="mt-3 block h-[3px] w-12 rounded-full bg-[#C62828]" />

      {status === "sent" ? (
        <div data-testid="enquiry-success" className="mt-8 flex flex-col items-center rounded-2xl bg-[#C62828]/5 px-6 py-10 text-center">
          <CheckCircle2 size={44} className="text-[#C62828]" />
          <p className="mt-4 text-lg font-bold text-[#1F1F1F]">{s.form_success_heading}</p>
          <p className="mt-2 text-sm text-[#666666]">
            {s.form_success_text} For urgent requirements, call us at {s.phone_1}.
          </p>
          <button
            data-testid="enquiry-another-button"
            onClick={() => setStatus("idle")}
            className="mt-6 rounded-full border border-[#C62828] px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-[#C62828] transition-all duration-300 hover:bg-[#C62828] hover:text-white"
          >
            Send Another Enquiry
          </button>
        </div>
      ) : (
        <form data-testid="enquiry-form" onSubmit={submit} className="mt-7 grid gap-5 sm:grid-cols-2">
          <Field label={s.form_label_name} required testid="field-name">
            <input data-testid="input-name" required value={form.full_name} onChange={set("full_name")} placeholder="Your full name" className={inputCls} />
          </Field>
          <Field label={s.form_label_email} required testid="field-email">
            <input data-testid="input-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
          </Field>
          <Field label={s.form_label_phone} required testid="field-phone">
            <input data-testid="input-phone" type="tel" required value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputCls} />
          </Field>
          <Field label={s.form_label_event_type} required testid="field-event-type">
            <select data-testid="input-event-type" required value={form.event_type} onChange={set("event_type")} className={inputCls}>
              <option value="" disabled>Select event type</option>
              {eventTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </Field>
          <Field label={s.form_label_event_date} testid="field-event-date">
            <input data-testid="input-event-date" type="date" value={form.event_date} onChange={set("event_date")} className={inputCls} />
          </Field>
          <Field label={s.form_label_location} testid="field-location">
            <input data-testid="input-location" value={form.location} onChange={set("location")} placeholder="City / Venue" className={inputCls} />
          </Field>
          <div className="sm:col-span-2">
            <Field label={s.form_label_message} required testid="field-message">
              <textarea data-testid="input-message" required rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about your event, audience, and requirements..." className={`${inputCls} resize-none`} />
            </Field>
          </div>
          {error && (
            <p data-testid="enquiry-error" className="text-sm font-medium text-[#C62828] sm:col-span-2">
              Something went wrong. Please try again, call us at{" "}
              <a href={`tel:${s.phone_1.replace(/\s/g, "")}`} className="underline">{s.phone_1}</a>{" "}
              or email{" "}
              <a href={`mailto:${s.email}`} className="underline">{s.email}</a>.
            </p>
          )}
          <div className="sm:col-span-2">
            <button
              data-testid="enquiry-submit-button"
              type="submit"
              disabled={status === "sending"}
              className="inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-[#C62828] px-8 py-4 text-sm font-bold uppercase tracking-wider text-white shadow-[0_14px_35px_rgba(198,40,40,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#a51f1f] disabled:opacity-60 sm:w-auto"
            >
              <Send size={16} strokeWidth={2.5} />
              {status === "sending" ? "Sending..." : s.contact_form_button}
            </button>
          </div>
        </form>
      )}
    </motion.div>
  );
};

const ContactSection = ({ s }) => (
  <section data-testid="contact-main" className="bg-[#F8F8F8] py-16 lg:py-20">
    <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-2 lg:gap-14 lg:px-8">
      <div>
        <motion.div {...fadeUp()}>
          <h2 className="text-3xl font-extrabold tracking-tight text-[#1F1F1F] sm:text-4xl">
            {s.contact_intro_heading}
          </h2>
          <span className="mt-4 block h-[4px] w-16 rounded-full bg-[#C62828]" />
          <p className="mt-5 text-base leading-relaxed text-[#666666]">{s.contact_intro_text}</p>
        </motion.div>
        <div className="mt-8 grid gap-5 sm:grid-cols-2">
          <InfoCard icon={MapPin} title="Visit Us" testid="info-visit">
            {s.address}
          </InfoCard>
          <InfoCard icon={Phone} title="Call Us" testid="info-call">
            <a data-testid="phone-link-1" href={`tel:${s.phone_1.replace(/\s/g, "")}`} className="block transition-colors hover:text-[#C62828]">{s.phone_1}</a>
            <a data-testid="phone-link-2" href={`tel:${s.phone_2.replace(/\s/g, "")}`} className="mt-1 block transition-colors hover:text-[#C62828]">{s.phone_2}</a>
          </InfoCard>
          <InfoCard icon={Mail} title="Email Us" testid="info-email">
            <a data-testid="email-link" href={`mailto:${s.email}`} className="break-all transition-colors hover:text-[#C62828]">{s.email}</a>
          </InfoCard>
          <InfoCard icon={Clock} title="Working Hours" testid="info-hours">
            {s.working_hours}
          </InfoCard>
        </div>
      </div>
      <div id="enquiry-form" className="scroll-mt-40 lg:scroll-mt-[170px]">
        <EnquiryForm s={s} />
      </div>
    </div>
  </section>
);

const MapSection = ({ s }) => {
  const query = encodeURIComponent(s.address);
  return (
    <section data-testid="find-us-section" className="bg-white py-16 lg:py-20">
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 lg:grid-cols-2 lg:gap-14 lg:px-8">
        <motion.div {...fadeUp()} className="overflow-hidden rounded-2xl shadow-[0_16px_45px_rgba(0,0,0,0.1)] ring-1 ring-[#F0F0F0]">
          <iframe
            data-testid="map-embed"
            title="Wonder Events office location"
            src={`https://www.google.com/maps?q=${query}&output=embed`}
            className="h-[320px] w-full border-0 sm:h-[400px]"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </motion.div>
        <motion.div {...fadeUp(0.12)}>
          <span className="inline-block rounded-full bg-[#C62828]/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-[#C62828]">
            Our Location
          </span>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-[#1F1F1F] sm:text-4xl">
            {s.findus_heading}
          </h2>
          <span className="mt-4 block h-[4px] w-16 rounded-full bg-[#C62828]" />
          <p className="mt-5 flex items-start gap-3 text-base leading-relaxed text-[#666666]">
            <MapPin size={20} className="mt-1 shrink-0 text-[#C62828]" />
            {s.address}
          </p>
          <a
            data-testid="get-directions-button"
            href={`https://www.google.com/maps/search/?api=1&query=${query}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-7 inline-flex items-center gap-2.5 rounded-full bg-[#C62828] px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-white shadow-[0_14px_35px_rgba(198,40,40,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#a51f1f]"
          >
            {s.get_directions_text}
            <ArrowRight size={16} strokeWidth={2.5} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

const FinalCTA = ({ s }) => (
  <section data-testid="contact-cta-section" className="bg-[#C62828] py-20 lg:py-24">
    <div className="mx-auto max-w-4xl px-5 text-center">
      <motion.div {...fadeUp()}>
        <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
          {s.contact_cta_heading}
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base text-white/85 sm:text-lg">{s.contact_cta_text}</p>
        <a
          data-testid="contact-final-cta-button"
          href="#enquiry-form"
          className="mt-9 inline-flex items-center gap-2.5 rounded-full bg-white px-8 py-4 text-sm font-bold uppercase tracking-wider text-[#C62828] shadow-[0_14px_35px_rgba(0,0,0,0.25)] transition-all duration-300 hover:-translate-y-1 hover:bg-[#1F1F1F] hover:text-white"
        >
          {s.contact_cta_button}
          <ArrowRight size={17} strokeWidth={2.5} />
        </a>
      </motion.div>
    </div>
  </section>
);

const Contact = () => {
  const s = useSiteContent();

  return (
    <div data-testid="contact-page" className="bg-white">
      <Header />
      <main>
        <Hero s={s} />
        <ContactSection s={s} />
        <MapSection s={s} />
        <FinalCTA s={s} />
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
